import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import { Message } from 'botframework-directlinejs';
import { ReferenceList, ReferenceGroup } from '@fluentai/react-copilot';
import {
  mergeClasses,
  useId,
  Tooltip,
  ToggleButton,
} from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import _ from 'underscore';
import Markdown from 'markdown-to-jsx';
import { Reference } from '@fluentai/reference';
import { Open16Regular, InfoRegular } from '@fluentui/react-icons';

import { CopyButton } from '../../CopyButton';
import { FeedbackButtons, IFeedbackData } from '../../FeedbackButtons';
import {
  FeedbackType,
  StreamingStatusType,
  IBotOutgoingMessageValue,
  IResponseSources,
} from '../../botTypes';
import { DirectLineContext } from '../direct-line-provider/direct-line-context';
import { BaseMessage, IBaseMessageProps } from './base-message';
import { AnchorInNewWindow } from './anchor-in-new-window';
import { useAIMessageStyles } from './use-ai-message-styles';
import { getDisclaimerString, shouldFeatureShowDisclaimer } from '../../utils';

export interface IAIMessage extends Omit<IBaseMessageProps, 'isAIMessage'> {
  message: Message;
  children?: React.ReactNode;
  className?: string;
  showDataDisclaimer?: boolean;
  showReferences?: boolean;
  showFeedback?: boolean;
  showCopyButton?: boolean;
  isShowFeaturesInHint?: boolean;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  onFeedbackButtonClick?(data: IFeedbackData, message: Message): void;
  onReferenceClick?(url: string): void;
}

export const AIMessage: React.FC<IAIMessage> = React.memo(({
  children,
  className,
  message,
  showDataDisclaimer,
  showReferences,
  showFeedback,
  showCopyButton,
  isShowFeaturesInHint,
  action,
  footer,
  onFeedbackButtonClick,
  onReferenceClick,
}) => {
  const styles = useAIMessageStyles();
  const { directLine, userid, username } = useContext(DirectLineContext)!;
  const { Sources } = ((message.value || {}) as any) as IBotOutgoingMessageValue;
  const { Features } = ((message.value || {}) as any) as IBotOutgoingMessageValue;
  const i18n = useContext(I18nContext) as I18nModel;
  const onFeedbackButtonClickCb = useCallback((data: IFeedbackData) => {
    if (onFeedbackButtonClick) {
      onFeedbackButtonClick(data, message);
    }
  }, [message, onFeedbackButtonClick]);

  const referenceClick = useCallback((source: IResponseSources) => {
    const { Url: url } = ((source || {}) as any) as IResponseSources;

    if (onReferenceClick) {
      onReferenceClick(url);
    }

    if (directLine) {
      const messageReactionAction = {
        type: 'messageReaction',
        replyToId: message.id,
        from: {
          id: userid,
          name: username,
        },
        reactionsAdded: [{ type: 'CitationClicked' }],
      };

      directLine.postActivity((messageReactionAction as any)).subscribe();
    }

    window.open(url, '_blank');
  }, [directLine, message, onReferenceClick, userid, username]);

  const { text = '' } = message;
  const [isInert, setIsInert] = React.useState(true);
  const footerId = useId('footer');
  const streamingStatus = _.get(message, ['value', 'StreamingStatus']) as any;
  const ariaHiddenWhileLoading = streamingStatus !== StreamingStatusType.Completed;
  const shouldShowDisclaimer = useMemo(() => showDataDisclaimer || shouldFeatureShowDisclaimer(message), [message, showDataDisclaimer]);
  const disclaimerText = useMemo(() => getDisclaimerString(message), [message]);

  const showFooter = showReferences || showCopyButton || showFeedback || isShowFeaturesInHint;
  const currentFeedback: FeedbackType = _.get(message, ['value', 'currentFeedback']) as any;

  const finalFooter = showFooter ? (
    <div className={mergeClasses(styles.footer, 'footer')} id={footerId}>
      <div className={mergeClasses(styles.footerContent, 'footer-content')}>
        {(shouldShowDisclaimer) ? (
          <div className={mergeClasses(styles.dataDisclaimerContainer, 'data-disclaimer')}>
            <Tooltip
              content={i18n.getString(disclaimerText)}
              relationship="description"
              withArrow
            >
              <ToggleButton
                className={mergeClasses(styles.dataDisclaimerButton)}
                appearance="transparent"
                icon={<InfoRegular fontSize={12} className={styles.infoIcon} />}
                iconPosition="after"
              >
                {i18n.getString(_TL_('Content source'))}
              </ToggleButton>
            </Tooltip>
          </div>
        ) : null}
        {!showDataDisclaimer && showReferences && Sources && Sources.length > 0 ? (
          <ReferenceGroup
            label={i18n.getString(_TL_('Learn more'))}
            className={mergeClasses(styles.referenceGroup, 'reference-group')}
          >
            <ReferenceList>
              {Sources.map((source, idx) => (
                <div
                  key={source.Url}
                  {/* this workaround is so typescript takes in this property,
                  this fix will stop working once this is merged:
                  https://github.com/facebook/react/pull/24730 */
                  ...{ inert: isInert ? '' : undefined }}
                >
                  <Reference
                    className={styles.reference}
                    citation={null}
                    key={source.Url}
                    mode="sidecar"
                    media={null}
                    index={idx + 1}
                    detailsButton={null}
                    actions={<Open16Regular />}
                    label={source.Title}
                    title={source.Title}
                    role="link"
                    aria-label={`${i18n.getString(_TL_('Learn More About:'))} ${source.Title}`}
                    onClick={() => referenceClick(source)}
                    tabIndex={0}
                  />
                </div>
              ))}
            </ReferenceList>
          </ReferenceGroup>
        ) : null}
        <div
          className={mergeClasses(styles.buttons, 'copilot-footer-buttons')}
          data-features={isShowFeaturesInHint && Features?.join(',')}
        >
          {showCopyButton && <CopyButton messageText={message.text!} ariaHiddenWhileLoading={ariaHiddenWhileLoading} />}
          {showFeedback && <FeedbackButtons currentFeedback={currentFeedback} onFeedbackChange={onFeedbackButtonClickCb} ariaHiddenWhileLoading={ariaHiddenWhileLoading} />}
        </div>
      </div>
      {footer}
    </div>
  ) : null;

  const updateInertValueOnClick = useCallback((learnMoreButton: Element) => {
    setIsInert(_.get(learnMoreButton, ['ariaExpanded']) === 'true');
  }, [setIsInert]);

  useEffect(() => {
    const learnMoreButton = footerId && document.getElementById(footerId)?.getElementsByClassName('fai-ReferenceGroup__detailsButton')[0];
    if (learnMoreButton) {
      learnMoreButton.addEventListener('click', () => updateInertValueOnClick(learnMoreButton));
    } else {
      // if we can't listen to clicks on learn more, then we should not keep the links disabled
      setIsInert(false);
    }
  }, [updateInertValueOnClick, footerId]);

  return (
    <div aria-hidden={ariaHiddenWhileLoading}>
      <BaseMessage
        message={message}
        className={mergeClasses(className, 'ai-message')}
        footer={finalFooter}
        action={action}
      >
        {children || (
          <Markdown
            className="content"
            options={{
              overrides: {
                a: AnchorInNewWindow,
              },
            }}
          >
            {text}
          </Markdown>
        )}
      </BaseMessage>
    </div>
  );
});

AIMessage.defaultProps = {
  showReferences: true,
  showFeedback: true,
  showCopyButton: true,
  className: '',
  action: null,
  onFeedbackButtonClick: () => { },
  onReferenceClick: () => { },
  footer: null,
  isShowFeaturesInHint: false,
};
